import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Link, Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import QRCode from 'qrcode';
import { deviceAuthService } from '../api/deviceAuth';
import { setAuthToken } from '../api/client';
import { Logo } from '../assets/logo';

type Phase = 'loading' | 'waiting' | 'expired' | 'error';

export const DeviceLogin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [phase, setPhase] = useState<Phase>('loading');
    const [userCode, setUserCode] = useState('');
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    const deviceCodeRef = useRef('');
    const intervalSecondsRef = useRef(5);
    const pollTimerRef = useRef<number | null>(null);
    const countdownTimerRef = useRef<number | null>(null);
    const stoppedRef = useRef(false);

    const clearTimers = useCallback(() => {
        if (pollTimerRef.current !== null) {
            window.clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (countdownTimerRef.current !== null) {
            window.clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const startSession = useCallback(async () => {
        stoppedRef.current = false;
        clearTimers();
        setPhase('loading');
        setQrDataUrl('');
        setUserCode('');
        setExpiresAt(null);
        setSecondsLeft(null);

        try {
            const session = await deviceAuthService.start();
            if (stoppedRef.current) {
                return;
            }

            deviceCodeRef.current = session.device_code;
            intervalSecondsRef.current = Math.max(3, session.interval || 5);
            setUserCode(session.user_code);
            const expires = Date.now() + session.expires_in * 1000;
            setExpiresAt(expires);
            setSecondsLeft(session.expires_in);
            setPhase('waiting');

            const verifyUrl = `${window.location.origin}/link?code=${encodeURIComponent(session.user_code)}`;
            try {
                const dataUrl = await QRCode.toDataURL(verifyUrl, {
                    width: 220,
                    margin: 2,
                    errorCorrectionLevel: 'M',
                });
                if (!stoppedRef.current) {
                    setQrDataUrl(dataUrl);
                }
            } catch {
                // QR is optional; code entry still works
            }

            const schedulePoll = () => {
                pollTimerRef.current = window.setTimeout(async () => {
                    if (stoppedRef.current) {
                        return;
                    }
                    try {
                        const result = await deviceAuthService.poll(deviceCodeRef.current);
                        if (stoppedRef.current) {
                            return;
                        }
                        if (result.status === 'approved' && result.accessToken) {
                            clearTimers();
                            setAuthToken(result.accessToken);
                            enqueueSnackbar(t('deviceLogin.success'), { variant: 'success' });
                            navigate('/dashboard', { replace: true });
                            return;
                        }
                        if (result.status === 'expired') {
                            clearTimers();
                            setPhase('expired');
                            return;
                        }
                        schedulePoll();
                    } catch (err) {
                        if (stoppedRef.current) {
                            return;
                        }
                        if (axios.isAxiosError(err) && err.response?.status === 410) {
                            clearTimers();
                            setPhase('expired');
                            return;
                        }
                        schedulePoll();
                    }
                }, intervalSecondsRef.current * 1000);
            };

            schedulePoll();
        } catch {
            if (!stoppedRef.current) {
                setPhase('error');
            }
        }
    }, [clearTimers, enqueueSnackbar, navigate, t]);

    useEffect(() => {
        void startSession();
        return () => {
            stoppedRef.current = true;
            clearTimers();
        };
    }, [startSession, clearTimers]);

    useEffect(() => {
        if (phase !== 'waiting' || expiresAt === null) {
            return;
        }

        countdownTimerRef.current = window.setInterval(() => {
            const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            setSecondsLeft(left);
            if (left <= 0) {
                clearTimers();
                setPhase('expired');
            }
        }, 1000);

        return () => {
            if (countdownTimerRef.current !== null) {
                window.clearInterval(countdownTimerRef.current);
                countdownTimerRef.current = null;
            }
        };
    }, [phase, expiresAt, clearTimers]);

    const formatCountdown = (totalSeconds: number) => {
        const m = Math.floor(totalSeconds / 60);
        const s = totalSeconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 4 },
                    width: '100%',
                    maxWidth: 480,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    textAlign: 'center',
                }}
            >
                <Logo
                    sx={{
                        width: 48,
                        height: 48,
                        mb: 2,
                        color: 'text.primary',
                        display: 'block',
                        margin: '0 auto',
                    }}
                />
                <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('deviceLogin.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('deviceLogin.subtitle')}
                </Typography>

                {phase === 'loading' && (
                    <Box sx={{ py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {phase === 'waiting' && (
                    <>
                        {qrDataUrl && (
                            <Box
                                component="img"
                                src={qrDataUrl}
                                alt={t('deviceLogin.qrAlt')}
                                sx={{
                                    width: 220,
                                    height: 220,
                                    mb: 2,
                                    borderRadius: 2,
                                    bgcolor: '#fff',
                                }}
                            />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('deviceLogin.codeLabel')}
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: 4,
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                mb: 1,
                            }}
                        >
                            {userCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('deviceLogin.instructions', {
                                url: `${window.location.host}/link`,
                            })}
                        </Typography>
                        {secondsLeft !== null && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                {t('deviceLogin.expiresIn', { time: formatCountdown(secondsLeft) })}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                {t('deviceLogin.waiting')}
                            </Typography>
                        </Box>
                    </>
                )}

                {phase === 'expired' && (
                    <>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            {t('deviceLogin.expired')}
                        </Typography>
                        <Button variant="contained" onClick={() => void startSession()} sx={{ textTransform: 'none' }}>
                            {t('deviceLogin.retry')}
                        </Button>
                    </>
                )}

                {phase === 'error' && (
                    <>
                        <Typography color="error" sx={{ mb: 3 }}>
                            {t('deviceLogin.error')}
                        </Typography>
                        <Button variant="contained" onClick={() => void startSession()} sx={{ textTransform: 'none' }}>
                            {t('deviceLogin.retry')}
                        </Button>
                    </>
                )}

                <Typography variant="body2" sx={{ mt: 4 }}>
                    <Link component={RouterLink} to="/login" underline="hover">
                        {t('deviceLogin.backToLogin')}
                    </Link>
                </Typography>
            </Paper>
        </Box>
    );
};
